/**
 * `hot-formula-parser` only accepts variable names made of letters and
 * underscores. Anything else (`#`, `$`, `-`, `.`, `/`, digits, ...) makes the
 * tokenizer bail out and the whole expression evaluates to `#ERROR!`, which the
 * bulk-upload job silently turns into an empty target column.
 *
 * So a source column name has to be encoded into a legal identifier before it is
 * handed to the parser, and the encoding has to be deterministic - the front end
 * builds the formula string, the back end (`DataObjectBulkUploadJobProcessor`)
 * re-binds the same variable names when the job runs.
 */

const LEGAL_VARIABLE_NAME = /^[A-Za-z_]+$/;

// The package cannot tokenise digits inside a variable name, so they are
// transliterated to letters. Kept as-is for backwards compatibility with
// mappings that were already saved.
const DIGIT_TO_LETTER: Record<string, string> = {
    "1": "a",
    "2": "b",
    "3": "c",
    "4": "d",
    "5": "e",
    "6": "f",
    "7": "g",
    "8": "h",
    "9": "i",
    "0": "j",
};

/**
 * Stable 32-bit FNV-1a hash rendered as 7 lowercase letters, used to keep two
 * different source columns that sanitise to the same identifier apart
 * (e.g. `A#B` and `A$B` would both collapse to `_A_B_`).
 */
function hashSuffix(value: string): string {
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i += 1) {
        hash ^= value.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }

    let encoded = "";
    let remaining = hash;
    for (let i = 0; i < 7; i += 1) {
        encoded += String.fromCharCode(97 + (remaining % 26));
        remaining = Math.floor(remaining / 26);
    }
    return encoded;
}

/**
 * Encode a source column name into a variable name the formula parser accepts.
 * Exported so the sample-value preview, the mapping payload and any future
 * consumer all derive the exact same name from a given column.
 */
export function toFormulaVariableName(sourceColumn: string): string {
    const withoutDigits = sourceColumn
        .replaceAll(" ", "_")
        .replaceAll(/[0-9]/g, (matched) => DIGIT_TO_LETTER[matched] ?? "x")
        .trim();

    // Every remaining character outside [A-Za-z_] is illegal for the parser.
    const sanitised = withoutDigits.replaceAll(/[^A-Za-z_]/g, "_");

    if (sanitised === withoutDigits) {
        // Nothing beyond the legacy space/digit handling had to change, so the
        // name is byte-for-byte what previous versions produced.
        return `_${sanitised}_`;
    }

    // Illegal characters were rewritten - disambiguate with a hash of the
    // original column name so distinct columns never share a variable.
    return `_${sanitised}_${hashSuffix(sourceColumn)}_`;
}

/** True when the name can actually be evaluated by the formula parser. */
export function isLegalFormulaVariableName(name: string): boolean {
    return LEGAL_VARIABLE_NAME.test(name);
}

export default function convertFormulaBuilderToString(
    builder: IFormulaBuilder,
): {
    formula: string;
    variables: IFormulaVariable[];
} {
    const variables: IFormulaVariable[] = [];

    function builderToString(b: IFormulaBuilder): string {
        // Type string and variable do not have children
        if (b.formulaFunction.type === "string") {
            // Check if string is already quoted.
            // Split into 2 tests to prevent mixed single and double quotes.
            if (
                /^'.*'$/.test(b.formulaFunction.value) ||
                /^".*"$/.test(b.formulaFunction.value)
            ) {
                return b.formulaFunction.value;
            }
            return `"${b.formulaFunction.value}"`;
        } else if (b.formulaFunction.type === "variable") {
            const trimmedVariable = toFormulaVariableName(
                b.formulaFunction.value,
            );
            variables.push({
                value: b.formulaFunction.value,
                name: trimmedVariable,
                valueType: "DATA_COLUMN",
            });
            return trimmedVariable;
        } else if (b.formulaFunction.type === "formula") {
            // For Type formula
            const children: string[] = [];
            if (b.children) {
                b.children.forEach((child) => {
                    const childString = builderToString(child);
                    if (childString) {
                        children.push(childString);
                    }
                });
            }
            return `${b.formulaFunction.value}(${children.join(",")})`;
        }
        // For other non-supported types
        return "";
    }

    const formula = builderToString(builder);

    return {
        formula,
        variables,
    };
}
